import { createSelector } from 'reselect';
import { AccessibilityType, ApplicationConfig, ApplicationStateType, JobTableName } from './application.types';
import { CommunicationActionPlacement } from '../../screens/common/Communication/CommunicationTrigger';
import { resolveHiddenFields } from '../../fieldVisibility/fieldVisibility';

const getSubState = (state: any) => state.application as ApplicationStateType;

const isFocused = createSelector(getSubState, (state) => state.isFocused);
const isLoaded = createSelector(getSubState, (state) => state.loaded);
const getApplicationName = createSelector(getSubState, (state) => state.name);
const getApplicationId = createSelector(getSubState, (state) => state.applicationId);

const getApplicationConfig = createSelector(getSubState, (state) => state.config as ApplicationConfig);

const getMeterReadingConfig = createSelector(getApplicationConfig, (config) => config?.meterReading);

const getUnknownUserReadingConfig = createSelector(getApplicationConfig, (config) => config?.unknownUserReading);

const isQueryPostDecimalsEnabled = createSelector(getMeterReadingConfig, (config) => config?.queryPostDecimals ?? false);

const isMaskPostDecimalsEnabled = createSelector(getMeterReadingConfig, (config) => config?.maskPostDecimals ?? false);

const getCustomComments = createSelector(getMeterReadingConfig, (config) => config?.defaultComments);

const getAiImageEvaluationEnabled = createSelector(getMeterReadingConfig, (config) => config?.aiImageEvaluationEnabled ?? false);
const getAiImageEvaluationAllowDesktop = createSelector(getMeterReadingConfig, (config) => config?.aiImageEvaluationAllowDesktop ?? false);
const getAiImageEvaluationAllowGallery = createSelector(getMeterReadingConfig, (config) => config?.aiImageEvaluationAllowGallery ?? false);
const getAiImageEvaluationMeterNumberMatchTrailingCharsCount = createSelector(
    getMeterReadingConfig,
    (config): number | undefined => config?.aiImageEvaluationMeterNumberMatchTrailingCharsCount,
);
const getAiImageEvaluationAbortAfterMs = createSelector(getMeterReadingConfig, (config) => config?.aiImageEvaluationAbortAfterMs ?? 5000);

export const AI_LIVE_ASSISTANT_DEFAULTS = {
    pollIntervalMs: 1000,
    // Must scale with AI_LIVE_FRAME_MAX_DIMENSION: 1280px frames take the detection service
    // noticeably longer than the original 640px ones — a shorter timeout silently aborts most
    // responses and the assistant appears to "pause" (sporadic boxes, delayed recognition).
    liveTimeoutMs: 6000,
    frameDiffThreshold: 8,
    confidenceThreshold: 0.8,
    stableFrames: 2,
    trackerMinFps: 12,
    // How many independent images must report the SAME value (reading + obis) before it is
    // accepted — filters random OCR outliers. 1 = accept immediately (pre-consensus behavior).
    consensusCount: 2,
};

// Opt-in feature: the assistant only appears when the (job-merged) backend config explicitly
// enables it. NOTE: the flag does not exist in the ksa-pro-backend ApplicationInfo model yet —
// until it is added there, no customer config can actually deliver it.
const isAiLiveAssistantEnabled = createSelector(getMeterReadingConfig, (config) => config?.aiLiveAssistantEnabled ?? false);

const getAiLiveAssistantConfig = createSelector(getMeterReadingConfig, (config) => ({
    ...AI_LIVE_ASSISTANT_DEFAULTS,
    ...(config?.aiLiveAssistant ?? {}),
}));

const getImprintConfig = createSelector(getApplicationConfig, (config) => config?.legal?.imprint);
const getPrivacyConfig = createSelector(getApplicationConfig, (config) => config?.legal?.privacy);
const getAccessibilityConfig = createSelector(
    getApplicationConfig,
    (config): AccessibilityType | undefined =>
        config?.legal?.accessibility ?? {
            enabled: false,
            legals: [
                {
                    text: 'Barrierefreie Webangebote Gesetz (BfWebG)',
                },
                {
                    text: 'Web Content Accessibility Guidelines 2.2 (WCAG 2.2) (AA)',
                },
            ],
            // alternatives: [
            //     {
            //         title: 'Kundenservice',
            //         description: 'Unser Kundenservice ist montags bis freitags zwischen 7 und 18 Uhr für Sie erreichbar.',
            //         link: 'tel:034197595080',
            //         text: '0341-975 950 80',
            //     },
            //     {
            //         title: 'Automatischer Sprachcomputer',
            //         description:
            //             'Unser automatischer Sprachcomputer führt Sie durch die Ablesung. Ihre Daten zur Anmeldung finden Sie auf dem Brief, den Sie von uns erhalten haben.',
            //         link: 'tel:034197595080',
            //         text: '0341-975 950 80',
            //     },
            // ],
            contact: {
                email: {
                    link: 'barriere@envago.de',
                    text: 'barriere@envago.de',
                },
                phone: {
                    link: '034197595080',
                    text: '0341-975 950 80',
                },
                postal: 'envago GmbH, Forststr. 9, 04229 Leipzig',
                person: {
                    text: 'Robert Kunkel',
                },
            },
            enforcementOffice: {
                name: 'Geschäftsstelle des Landesbeauftragten für Inklusion der Menschen mit Behinderungen Durchsetzungsstelle',
                contact: 'Dr. Kirstin Wappler',
                postal: 'Archivstraße 1, 01097 Dresden',
                phone: {
                    link: '035156410713',
                    text: '0351 564-10713',
                },
                email: {
                    link: 'durchsetzungsstelle@sk.sachsen.de',
                    text: 'durchsetzungsstelle@sk.sachsen.de',
                },
                web: {
                    link: 'https://www.durchsetzungsstelle.sachsen.de/',
                    text: 'www.durchsetzungsstelle.sachsen.de',
                },
            },
            companyName: 'envago GmbH',
        },
);
const getFeaturesConfig = createSelector(getApplicationConfig, (config) => config?.features);
const getCommunicationSettingsConfig = createSelector(getApplicationConfig, (config) => config?.communicationSettings);

const isSendingCrmRegisteredUserReadingReceiptAllowed = createSelector(
    getCommunicationSettingsConfig,
    (config) => config?.sendingCrmRegisteredUserReadingReceiptAllowed ?? false,
);

const isSendingCrmRegisteredUserReadingReceiptVisible = (placement: CommunicationActionPlacement) =>
    createSelector(getCommunicationSettingsConfig, (config) => {
        switch (placement) {
            case 'HOME': {
                return config?.showSendingReadingReceiptOnHomeScreen ?? false;
            }
            case 'JOB': {
                return config?.showSendingReadingReceiptOnJobScreen ?? false;
            }
            case 'MENU': {
                return config?.showSendingReadingReceiptOnMainMenu ?? false;
            }
            default:
                return false;
        }
    });

const getCRMSettingsConfig = createSelector(getApplicationConfig, (config) => config?.crmSettings);

const isCRMRegistrationAllowed = createSelector(getCRMSettingsConfig, (state) => state?.registrationEnabled ?? false);

const isCRMRegistrationVisible = (placement: CommunicationActionPlacement) =>
    createSelector(getCRMSettingsConfig, (config) => {
        switch (placement) {
            case 'HOME': {
                return config?.showRegistrationOnHomeScreen ?? false;
            }
            case 'JOB': {
                return config?.showRegistrationOnJobScreen ?? false;
            }
            case 'MENU': {
                return config?.showRegistrationOnMainMenu ?? false;
            }
            default:
                return false;
        }
    });

const isShowModalAfterLastJobCompletedEnabled = createSelector(getCRMSettingsConfig, (config) => config?.showModalAfterLastJobCompleted ?? false);

const getCRMRegistrationURL = createSelector(getCRMSettingsConfig, (config) => config?.externalRegistrationOptInBaseUrl);

const isGlobalReshareAllowed = createSelector(getFeaturesConfig, (state) => state?.reshareAllowed);

const isUnknownReadingsEnabled = createSelector(getFeaturesConfig, (features) => features?.unknownReadingsAllowed);

const isEvaluationEnabled = createSelector(getFeaturesConfig, (features) => features?.evaluationEnabled);

const isEvaluationUseHistoricReadingsAllowed = createSelector(getFeaturesConfig, (features) => features?.evaluationUseHistoricReadings ?? true);

const isEvaluationShowCurrentReadingsAllowed = createSelector(getFeaturesConfig, (features) => features?.evaluationShowCurrentReadings ?? true);

const isEvaluationShowOnlyDoneJobsAllowed = createSelector(getFeaturesConfig, (features) => features?.evaluationShowOnlyDoneJobs ?? true);

const evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays = createSelector(getFeaturesConfig, (features) => {
    return features?.evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays === undefined
        ? 14
        : features?.evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays;
});

const isUnknownReadingsImageRequired = createSelector(getFeaturesConfig, (features) => features?.unknownReadingsImageRequired ?? false);

const unknownReadingsMaxDaysInPast = createSelector(getFeaturesConfig, (features) => features?.unknownReadingsMaxDaysInPast ?? 30);
const unknownReadingsMaxDaysInFuture = createSelector(getFeaturesConfig, (features) => features?.unknownReadingsMaxDaysInFuture ?? 0);

const loginUsernameMinLength = createSelector(getFeaturesConfig, (features) => features?.loginUsernameMinLength ?? 5);
const loginPasswordMinLength = createSelector(getFeaturesConfig, (features) => features?.loginPasswordMinLength ?? 5);
const isLoginPasswordInputAsCleartextEnabled = createSelector(getFeaturesConfig, (features) => features?.loginPasswordInputAsCleartext);

const isRedirectToJobsScreenOnLoginEnabled = createSelector(getFeaturesConfig, (features) => features?.redirectToJobsScreenOnLoginEnabled ?? false);

const isMultipleAccountsLoginEnabled = createSelector(getFeaturesConfig, (features) => features?.multipleAccountsLoginEnabled ?? true);

const isPreselectJobIfSingleEnabled = createSelector(getFeaturesConfig, (features) => features?.preselectJobIfSingleEnabled ?? false);

const isSendSubaccountReminderMailtoLinkEnabled = createSelector(getFeaturesConfig, (features) => features.sendSubaccountReminderMailtoLinkEnabled ?? false);

const isExcelExportAllowed = createSelector(getFeaturesConfig, (features) => features?.excelExportAllowed ?? false);

const isExcelImportAllowed = createSelector(getFeaturesConfig, (features) => features?.excelImportAllowed ?? false);

const isAssignmentPresetsAllowed = createSelector(getFeaturesConfig, (features) => features?.assignmentPresetsAllowed ?? false);
const isMeloAttributeAllowed = createSelector(getFeaturesConfig, (features) => features?.meloAttributeAllowed ?? false);
const getConfiguredMeloAttributeTypes = createSelector(getFeaturesConfig, (features) => {
    const types = features?.meloAttributeTypes;
    return types && types.length > 0 ? types : ['NOTE'];
});

const isUnknownUserReadingAllowed = createSelector(getApplicationConfig, (config) => config?.unknownUserReading?.unknownUserReadingAllowed || false);

const isSendingUnknownUserReadingReceiptAllowed = createSelector(
    getCommunicationSettingsConfig,
    (config) => config?.sendingUnknownUserReadingReceiptAllowed ?? false,
);

const getDefaultJobSortSettings = createSelector(getFeaturesConfig, (state) => state.jobSortSettings);

const getFieldVisibilityConfig = createSelector(getFeaturesConfig, (features) => features?.fieldVisibility);

const getJobTablesConfig = createSelector(getFieldVisibilityConfig, (fieldVisibility) => fieldVisibility?.jobTables);

// Die Kaskade steckt in resolveHiddenFields, nicht hier: dort ist die Logik und
// sie muss ohne Store testbar sein. Dieser Selector memoisiert nur — die Menge
// muss referenzstabil sein, weil die Tabellen ihre Spaltenliste darauf memoisieren.
const getHiddenFields = createSelector(getFieldVisibilityConfig, (fieldVisibility) =>
    resolveHiddenFields(fieldVisibility?.globalHidden),
);

// Fabrik-Selektor analog zu sharesSelectors.getSharedJobIdsForAccount: pro Aufruf
// entsteht ein eigener Selektor. Das Ergebnis ist die unveränderte Referenz aus
// dem Store, deshalb löst useSelector dadurch keinen zusätzlichen Re-Render aus.
const getJobTableConfig = (name: JobTableName) => createSelector(getJobTablesConfig, (jobTables) => jobTables?.[name]);

const getCameraPermission = createSelector(getSubState, (state) => state?.cameraPermission);

export default {
    getApplicationConfig,

    isFocused,
    isLoaded,
    getApplicationName,
    getApplicationId,

    getImprintConfig,
    getPrivacyConfig,
    getAccessibilityConfig,
    getFeaturesConfig,
    getUnknownUserReadingConfig,

    getAiImageEvaluationEnabled,
    getAiImageEvaluationAllowDesktop,
    getAiImageEvaluationAllowGallery,
    getAiImageEvaluationMeterNumberMatchTrailingCharsCount,
    getAiImageEvaluationAbortAfterMs,

    isAiLiveAssistantEnabled,
    getAiLiveAssistantConfig,

    isEvaluationEnabled,
    isEvaluationUseHistoricReadingsAllowed,
    isEvaluationShowCurrentReadingsAllowed,
    isEvaluationShowOnlyDoneJobsAllowed,
    evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays,

    isPreselectJobIfSingleEnabled,
    isUnknownReadingsEnabled,
    isUnknownReadingsImageRequired,
    unknownReadingsMaxDaysInPast,
    unknownReadingsMaxDaysInFuture,

    loginUsernameMinLength,
    loginPasswordMinLength,
    isLoginPasswordInputAsCleartextEnabled,

    isMultipleAccountsLoginEnabled,
    isRedirectToJobsScreenOnLoginEnabled,

    isGlobalReshareAllowed,

    getCustomComments,
    isQueryPostDecimalsEnabled,
    isMaskPostDecimalsEnabled,

    isSendSubaccountReminderMailtoLinkEnabled,

    isExcelExportAllowed,

    isExcelImportAllowed,

    isAssignmentPresetsAllowed,
    isMeloAttributeAllowed,
    getConfiguredMeloAttributeTypes,

    isUnknownUserReadingAllowed,

    isCRMRegistrationAllowed,
    isCRMRegistrationVisible,
    getCRMRegistrationURL,

    isShowModalAfterLastJobCompletedEnabled,

    isSendingCrmRegisteredUserReadingReceiptAllowed,
    isSendingCrmRegisteredUserReadingReceiptVisible,
    isSendingUnknownUserReadingReceiptAllowed,

    getDefaultJobSortSettings,
    getJobTableConfig,
    getJobTablesConfig,
    getHiddenFields,

    getCameraPermission,
} as const;
